import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { SearchSuggest, SearchNotice } from "@/components/SearchSuggest";
import { useAuth } from "@/context/AuthContext";
import { CompactWorkshopCard } from "@/components/CompactWorkshopCard";
import { useMyWorkshopStatus } from "@/lib/myWorkshopStatus";
import { ExploreFilters, PRICE_MAX, SLOTS } from "@/components/ExploreFilters";
import { ExploreLocation } from "@/components/ExploreLocation";
import { NearbyWorkshops } from "@/components/NearbyWorkshops";
import { LocationNotice } from "@/components/LocationNotice";
import { useGeo, rememberPlace, readLast, APPLIED } from "@/lib/geo";
import { PreferencesModal } from "@/components/PreferencesModal";
import { CommunitySection } from "@/components/CommunitySection";
import { RotatingPhrase } from "@/components/RotatingPhrase";
import { FeaturedMaestro } from "@/components/FeaturedMaestro";
import { CatalogPager, LoadMoreButton, PAGE_SIZE } from "@/components/CatalogPager";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sparkles, ArrowRight, CalendarClock, Smartphone, ShieldCheck, Gift,
  Store, MessageCircle, Menu, LayoutGrid, Megaphone, Heart,
} from "lucide-react";
import { CLP } from "@/lib/format";
import { Switch } from "@/components/ui/switch";

const SORTS = [
  { value: "recent", label: "Más recientes" },
  { value: "rating", label: "Mejor evaluados" },
  { value: "soon", label: "Próximos a iniciar" },
  { value: "price_asc", label: "Precio: menor a mayor" },
  { value: "price_desc", label: "Precio: mayor a menor" },
];
const HERO_KEY = "vt-hero";

function useHeroImage() {
  const [src, setSrc] = useState(() => sessionStorage.getItem(HERO_KEY));
  useEffect(() => {
    if (src) return;
    api.get("/hero-photos").then(({ data }) => {
      const list = data.photos || [];
      const pick = list.length ? list[Math.floor(Math.random() * list.length)] : "/images/hero-ceramica.webp";
      sessionStorage.setItem(HERO_KEY, pick);
      setSrc(pick);
    }).catch(() => setSrc("/images/hero-ceramica.webp"));
  }, [src]);
  return src ? fileUrl(src) : null;
}

const BENEFIT_TONES = ["bg-primary/10 text-primary", "bg-sun/30 text-[#7a5200] dark:bg-sun/20 dark:text-sun", "bg-coral/15 text-coral", "bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300"];

const BENEFITS = [
  { icon: CalendarClock, title: "Encuentra tu próximo taller en segundos", text: "Explora experiencias locales, revisa fechas disponibles en tiempo real y asegura tu lugar al instante." },
  { icon: Smartphone, title: "Todo en tu teléfono", text: "Recibe tu ticket de reserva y confirma tu asistencia directamente. Olvídate de buscar correos a última hora." },
  { icon: ShieldCheck, title: "Transparencia y tranquilidad", text: "Si surge un imprevisto, cancela tu sesión dentro del plazo con un clic. Libera tu cupo al instante y recibe créditos para usarlos en tu próximo taller." },
  { icon: Gift, title: "Regala experiencias", text: "¿Quieres sorprender a alguien? Reserva un cupo como regalo y envía una entrada personalizada con QR directamente a esa persona especial." },
];

function inAvailability(w, slots) {
  if (!slots.length) return true;
  return (w.time_slots || []).some((s) => slots.includes(s));
}

export default function Catalog() {
  const { user, loading: authLoading } = useAuth();
  const heroSrc = useHeroImage();
  const prefs = useMemo(() => user?.preferences || [], [user]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [workshops, setWorkshops] = useState([]);
  const [searchInfo, setSearchInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrefs, setShowPrefs] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [personalized, setPersonalized] = useState(false);

  // Filtros
  const [q, setQ] = useState("");
  const [cats, setCats] = useState(() => (searchParams.get("categoria") || "").split(",").filter(Boolean));
  const [region, setRegion] = useState(searchParams.get("region") || "all");
  const [commune, setCommune] = useState(searchParams.get("comuna") || "all");
  const [level, setLevel] = useState(searchParams.get("nivel") || "all");
  const [maxPrice, setMaxPrice] = useState(Number(searchParams.get("precio")) || PRICE_MAX);
  const [minPrice, setMinPrice] = useState(Number(searchParams.get("precio_min")) || 0);
  const [priceBounds, setPriceBounds] = useState(null);
  useEffect(() => { api.get("/catalog/price-range").then((r) => setPriceBounds(r.data)).catch(() => {}); }, []);
  const [availability, setAvailability] = useState(() => (searchParams.get("horario") || "").split(",").filter((s) => SLOTS.includes(s)));
  const [sort, setSort] = useState(searchParams.get("orden") || "recent");

  // Feed personalizado: al entrar por primera vez, un alumno con intereses ve sus categorías preseleccionadas.
  // Si ya ajustó filtros en esta sesión, se respetan al volver al catálogo.
  const [ready, setReady] = useState(false);
  const prevUser = useRef(undefined);
  useEffect(() => {
    if (authLoading) return;
    const loggedOut = prevUser.current && !user;
    prevUser.current = user;
    if (loggedOut) {
      setQ(""); setCats([]); setRegion("all"); setCommune("all"); setLevel("all");
      setMaxPrice(PRICE_MAX); setMinPrice(0); setAvailability([]); setSort("recent"); setPersonalized(false);
      sessionStorage.removeItem("catalog-filters");
      return;
    }
    api.get("/categories").then((r) => {
      setCategories(r.data);
      const urlCat = searchParams.get("categoria");
      const hasUrlFilters = ["categoria", "region", "comuna", "nivel", "precio", "horario", "orden"].some((k) => searchParams.get(k));
      const saved = JSON.parse(sessionStorage.getItem("catalog-filters") || "null");
      if (urlCat) {
        setCats(urlCat.split(",").filter((c) => r.data.includes(c)));
      } else if (saved && !hasUrlFilters) {
        setCats((saved.cats || []).filter((c) => r.data.includes(c)));
        setRegion(saved.region || "all"); setCommune(saved.commune || "all"); setLevel(saved.level || "all");
        setMaxPrice(saved.maxPrice || PRICE_MAX); setMinPrice(saved.minPrice || 0); setAvailability(Array.isArray(saved.availability) ? saved.availability : []);
        setSort(saved.sort || "recent"); setQ(saved.q || ""); setPersonalized(!!saved.personalized);
      } else if (!hasUrlFilters && user?.role === "customer" && prefs.length) {
        const matched = prefs.filter((p) => r.data.includes(p));
        if (matched.length) { setCats(matched); setPersonalized(true); }
      }
      setReady(true);
    });
  }, [user, authLoading]); // eslint-disable-line

  const page = Math.max(1, parseInt(searchParams.get("pagina"), 10) || 1);
  const geo = useGeo();
  const urlPlace = searchParams.get("region") || searchParams.get("comuna");
  useEffect(() => {
    if (!ready || urlPlace || sessionStorage.getItem(APPLIED)) return;
    if (geo.status === "located") {
      setRegion(geo.geo.region); setCommune(geo.geo.commune); sessionStorage.setItem(APPLIED, "1");
    } else if (geo.status === "manual") {
      const last = readLast();
      if (last && last.region !== "all" && !geo.cleared) { setRegion(last.region); setCommune(last.commune); }
      sessionStorage.setItem(APPLIED, "1");
    }
  }, [ready, geo.status]); // eslint-disable-line
  const clearLocation = () => { geo.clear(); setRegion("all"); setCommune("all"); };
  useEffect(() => {
    if (ready && sessionStorage.getItem(APPLIED)) rememberPlace(region, commune);
  }, [ready, region, commune]);
  const locMode = geo.status === "locating" && region === "all" ? "locating" : region !== "all" && !geo.cleared ? "located" : "manual";
  const restore = useRef((() => {
    try {
      const s = JSON.parse(sessionStorage.getItem("catalog-return") || "null");
      return s && s.search === window.location.search ? s : null;
    } catch { return null; }
  })());
  const [expanded, setExpanded] = useState(!!restore.current?.expanded);
  const filterKey = JSON.stringify([q, cats, region, commune, level, minPrice, maxPrice, availability, sort]);
  const prevKey = useRef(null);

  // Sincroniza filtros compartibles a la URL; si cambian los filtros se vuelve a la página 1
  useEffect(() => {
    const changed = prevKey.current !== null && prevKey.current !== filterKey;
    prevKey.current = filterKey;
    const p = {};
    if (!changed && page > 1) p.pagina = String(page);
    if (changed) setExpanded(false);
    if (cats.length) p.categoria = cats.join(",");
    if (region !== "all") p.region = region;
    if (commune !== "all") p.comuna = commune;
    if (level !== "all") p.nivel = level;
    if (maxPrice < PRICE_MAX) p.precio = String(maxPrice);
    if (minPrice > 0) p.precio_min = String(minPrice);
    if (availability.length) p.horario = availability.join(",");
    if (sort !== "recent") p.orden = sort;
    setSearchParams(p, { replace: true });
  }, [filterKey]); // eslint-disable-line

  useEffect(() => {
    if (!ready) return;
    sessionStorage.setItem("catalog-filters", JSON.stringify({ q, cats, region, commune, level, minPrice, maxPrice, availability, sort, personalized }));
  }, [filterKey, personalized, ready]); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (level !== "all") params.level = level;
      if (region !== "all") params.region = region;
      if (commune !== "all") params.commune = commune;
      if (maxPrice < PRICE_MAX) params.max_price = maxPrice;
      if (minPrice > 0) params.min_price = minPrice;
      if (sort) params.sort = sort;
      if (q.trim()) {
        const { data } = await api.get("/search", { params: { ...params, q: q.trim() } });
        setWorkshops(data.results);
        setSearchInfo(data);
      } else {
        const { data } = await api.get("/workshops", { params });
        setWorkshops(data);
        setSearchInfo(null);
      }
    } finally {
      setLoading(false);
    }
  }, [q, level, region, commune, minPrice, maxPrice, sort]);

  useEffect(() => { const t = setTimeout(load, q ? 450 : 250); return () => clearTimeout(t); }, [load]); // eslint-disable-line

  const toggleCat = (c) => { setPersonalized(false); setCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]); };
  const clearCats = () => { setPersonalized(false); setCats([]); };
  const myPrefs = useMemo(() => prefs.filter((p) => categories.includes(p)), [prefs, categories]);
  const backToPrefs = () => {
    setRegion("all"); setCommune("all"); setLevel("all");
    setMaxPrice(PRICE_MAX); setMinPrice(0); setAvailability([]); setQ(""); setSort("recent");
    setCats(myPrefs); setPersonalized(true);
    document.getElementById("talleres")?.scrollIntoView({ behavior: "smooth" });
  };
  const clearFilters = () => {
    setPersonalized(false);
    setCats([]); setLevel("all");
    setMaxPrice(PRICE_MAX); setMinPrice(0); setAvailability([]); setQ("");
  };

  const activeCount = useMemo(() => (
    (cats.length ? 1 : 0) +
    (level !== "all" ? 1 : 0) + (maxPrice < PRICE_MAX || minPrice > 0 ? 1 : 0) + (availability.length ? 1 : 0)
  ), [cats, level, minPrice, maxPrice, availability]);

  const displayed = useMemo(() => (
    workshops.filter((w) => inAvailability(w, availability) && (cats.length === 0 || cats.includes(w.category)))
  ), [workshops, availability, cats]);

  const prefSet = useMemo(() => new Set(prefs), [prefs]);
  const mine = useMyWorkshopStatus();
  const [hidePaid, setHidePaid] = useState(() => localStorage.getItem("hide-paid") === "1");
  const toggleHidePaid = (v) => { setHidePaid(v); localStorage.setItem("hide-paid", v ? "1" : "0"); };
  const paidInList = useMemo(() => displayed.filter((w) => mine.paid.has(w.id)).length, [displayed, mine.paid]);
  const shown = useMemo(() => (hidePaid ? displayed.filter((w) => !mine.paid.has(w.id)) : displayed), [hidePaid, displayed, mine.paid]);
  const suggested = useMemo(() => {
    if (user?.role !== "customer" || personalized || cats.length || !prefSet.size) return [];
    return workshops.filter((w) => prefSet.has(w.category) && !mine.paid.has(w.id)).slice(0, 8);
  }, [user, personalized, cats, prefSet, workshops, mine.paid]);

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = Math.min(shown.length, startIdx + (page === 1 && expanded ? 2 * PAGE_SIZE : PAGE_SIZE));
  const visible = shown.slice(startIdx, endIdx);

  const goToPage = (n) => {
    setExpanded(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (n > 1) next.set("pagina", String(n)); else next.delete("pagina");
      return next;
    });
    document.getElementById("talleres")?.scrollIntoView({ behavior: "smooth" });
  };
  const loadMore = () => (expanded ? goToPage(3) : setExpanded(true));
  const rememberPosition = () => sessionStorage.setItem("catalog-return", JSON.stringify({
    search: window.location.search, scrollY: window.scrollY, expanded,
  }));

  useEffect(() => {
    if (loading || !restore.current) return;
    const y = restore.current.scrollY;
    restore.current = null;
    sessionStorage.removeItem("catalog-return");
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [loading]);

  useEffect(() => {
    if (loading || sessionStorage.getItem("vt-scroll-talleres") !== "1") return;
    const t = setTimeout(() => {
      sessionStorage.removeItem("vt-scroll-talleres");
      const el = document.getElementById("talleres");
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: "smooth" });
    }, 400);
    return () => clearTimeout(t);
  }, [loading, workshops]);

  useEffect(() => {
    if (!loading && page > totalPages) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (totalPages > 1) next.set("pagina", String(totalPages)); else next.delete("pagina");
        return next;
      }, { replace: true });
    }
  }, [loading, page, totalPages]); // eslint-disable-line

  const filterProps = {
    categories, cats, toggleCat, clearCats, prefs,
    region, setRegion, commune, setCommune,
    level, setLevel, maxPrice, setMaxPrice, minPrice, setMinPrice, priceBounds, availability, setAvailability,
    onClear: clearFilters, activeCount,
    onEditPrefs: user?.role === "customer" ? () => { setSidebarOpen(false); setShowPrefs(true); } : undefined,
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative grain-overlay border-b border-border/60">
        <div className="absolute inset-0 overflow-hidden">
          {heroSrc && <img src={heroSrc} alt="" className="w-full h-full object-cover object-[center_35%] opacity-[0.22] saturate-[0.6] dark:opacity-15" data-testid="hero-watermark" />}
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-2xl mx-auto text-center animate-fade-up">
            <span className="block min-h-[3.5rem] sm:min-h-0 text-lg sm:text-xl font-semibold text-primary mb-4" data-testid="hero-tagline">
              <Sparkles className="inline w-5 h-5 text-coral mr-2 -mt-1" />Vive un mundo de <RotatingPhrase />
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Descubre, reserva y asiste <span className="text-primary">sin complicaciones</span>.
            </h1>
            <p className="mt-5 text-base text-muted-foreground max-w-xl mx-auto">
              <span className="block font-display font-bold text-foreground text-xl sm:text-2xl mb-3">
                El punto de encuentro para aprender, crear y conectar.
              </span>
              Talleres presenciales de joyería, cerámica, cocina y más, dictados por maestros locales.
              Elige tu taller, reserva tu cupo en segundos y vive una experiencia real.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <SearchSuggest q={q} setQ={setQ}
                onCategory={(c) => { setPersonalized(false); setCats([c]); }}
                onPlace={(r, c) => { setRegion(r || "all"); setCommune(c); }} />
              <Button size="lg" className="h-12 gap-2 shrink-0" data-testid="hero-explorar-btn"
                onClick={() => document.getElementById("talleres")?.scrollIntoView({ behavior: "smooth" })}>
                Explorar talleres <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="benefits-grid">
          {BENEFITS.map((b, i) => (
            <div key={i} data-testid={`benefit-${i}`}
              className="group rounded-2xl border border-border/70 bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(121,68,228,0.22)] animate-fade-up"
              style={{ animationDelay: `${i * 90}ms` }}>
              <span className={`grid place-items-center w-11 h-11 rounded-xl mb-4 transition-transform group-hover:scale-110 ${BENEFIT_TONES[i % BENEFIT_TONES.length]}`}>
                <b.icon className="w-5 h-5" />
              </span>
              <h3 className="font-display font-bold text-base leading-snug">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <FeaturedMaestro />

      {/* Explorar */}
      <section id="talleres" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 scroll-mt-20">
        {user?.role === "customer" && prefs.length === 0 && (
          <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="onboarding-prefs-banner">
            <Sparkles className="w-5 h-5 text-primary shrink-0" />
            <p className="text-sm flex-1">¿Qué te gustaría aprender hoy? Selecciona tus temas de interés para personalizar tu feed.</p>
            <Button size="sm" onClick={() => setShowPrefs(true)} data-testid="onboarding-prefs-btn">Elegir mis intereses</Button>
          </div>
        )}

        {personalized && (
          <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="personalized-banner">
            <Sparkles className="w-5 h-5 text-primary shrink-0" />
            <p className="text-sm flex-1">Personalizado para ti: estás viendo talleres de tus intereses ({prefs.filter((p) => categories.includes(p)).join(", ")}).</p>
            <Button variant="outline" size="sm" onClick={clearCats} data-testid="ver-todos-btn">
              <LayoutGrid className="w-4 h-4 mr-1.5" />Ver todos los talleres
            </Button>
          </div>
        )}

        {suggested.length > 0 && (
          <div className="mb-8" data-testid="suggested-section">
            <h2 className="mb-3 text-base md:text-lg font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Sugeridos para ti</h2>
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" onClickCapture={rememberPosition}>
              {suggested.map((w) => (
                <div key={w.id} className="snap-start shrink-0 w-[46%] sm:w-[30%] lg:w-[23%]" data-testid={`suggested-${w.id}`}>
                  <CompactWorkshopCard workshop={w} isInterest saved={mine.saved.has(w.id)} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-8 items-start">
          {/* Listado */}
          <div className="flex-1 min-w-0 relative">
            <SearchNotice info={searchInfo} />
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <button onClick={(e) => { const opening = !sidebarOpen; setSidebarOpen(opening); if (opening) e.currentTarget.scrollIntoView({ behavior: "smooth", block: "start" }); }} data-testid="expand-sidebar-btn" aria-label="Filtros" aria-expanded={sidebarOpen}
                className={`scroll-mt-20 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-primary transition-colors ${sidebarOpen ? "bg-primary/15" : "hover:bg-primary/10"}`}>
                <Menu className="w-6 h-6" strokeWidth={2.5} />
                <span className="text-sm font-semibold">Filtros{activeCount > 0 ? ` (${activeCount})` : ""}</span>
              </button>
              <ExploreLocation status={locMode} geo={geo.geo} region={region} setRegion={setRegion}
                commune={commune} setCommune={setCommune} onClear={clearLocation} onLocate={geo.locate} />
              {sidebarOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setSidebarOpen(false)} data-testid="filters-backdrop" />
                  <aside className="absolute left-0 top-14 z-40 w-[min(320px,calc(100vw-2rem))] max-h-[calc(100dvh-14rem)] lg:max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain rounded-2xl border border-gray-300 dark:border-gray-600 bg-card p-5 shadow-[0_18px_48px_-12px_rgba(0,0,0,0.25)] animate-in fade-in slide-in-from-top-2 duration-200" data-testid="filters-sidebar">
                    <ExploreFilters {...filterProps} onCollapse={() => setSidebarOpen(false)} />
                  </aside>
                </>
              )}
              <p className="text-sm text-muted-foreground hidden sm:block" data-testid="results-count">
                {loading ? "Cargando…" : `${shown.length} taller${shown.length === 1 ? "" : "es"}`}
              </p>

              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {user?.role === "customer" && myPrefs.length > 0 && !personalized && (
                  <Button variant="outline" size="sm" onClick={backToPrefs} className="border-primary/40 text-primary" data-testid="back-to-prefs-btn">
                    <Heart className="w-4 h-4 mr-1.5" />Volver a mis gustos
                  </Button>
                )}
                {cats.length > 0 && !personalized && (
                  <Button variant="ghost" size="sm" onClick={clearCats} data-testid="ver-todos-toolbar-btn">
                    <LayoutGrid className="w-4 h-4 mr-1.5" />Ver todos
                  </Button>
                )}
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[170px]" data-testid="sort-filter"><SelectValue placeholder="Ordenar" /></SelectTrigger>
                  <SelectContent>
                    {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {region === "all" && geo.status === "manual" && <LocationNotice reason={geo.reason} onRetry={geo.locate} onDismiss={geo.dismiss} />}

            {user?.role === "customer" && mine.paid.size > 0 && (
              <label className="mb-4 inline-flex items-center gap-2.5 rounded-full border bg-card px-3 py-1.5 text-sm cursor-pointer select-none" data-testid="hide-paid-filter">
                <Switch checked={hidePaid} onCheckedChange={toggleHidePaid} data-testid="hide-paid-switch" />
                Ocultar talleres que ya pagué
                <span className="text-muted-foreground" data-testid="hide-paid-count">({paidInList})</span>
              </label>
            )}

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
              </div>
            ) : shown.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground" data-testid="catalog-empty">
                {hidePaid && displayed.length > 0 ? "Ya reservaste todos los talleres de esta búsqueda."
                  : commune !== "all" ? <>Aún no hay talleres en <strong className="text-foreground">{commune}</strong>{activeCount > 0 ? " con esos filtros" : ""}.</>
                  : "No encontramos talleres con esos filtros."}
                {commune !== "all" && (
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <Button size="sm" onClick={() => setCommune("all")} data-testid="empty-see-region">Ver talleres de {region}</Button>
                    <Button variant="outline" size="sm" onClick={clearLocation} data-testid="empty-see-all">Ver todo Chile</Button>
                  </div>
                )}
                {activeCount > 0 && (
                  <div className="mt-4">
                    <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar filtros</Button>
                  </div>
                )}
                {commune !== "all" && !q.trim() && !(hidePaid && displayed.length > 0) && (
                  <NearbyWorkshops commune={commune} region={region} level={level} maxPrice={maxPrice < PRICE_MAX ? maxPrice : null}
                    keep={(w) => inAvailability(w, availability) && (cats.length === 0 || cats.includes(w.category)) && !(hidePaid && mine.paid.has(w.id))}
                    cardProps={(w) => ({ isInterest: prefSet.has(w.category), paid: mine.paid.has(w.id), saved: mine.saved.has(w.id) })} />
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="workshop-grid" onClickCapture={rememberPosition}>
                {visible.map((w) => <CompactWorkshopCard key={w.id} workshop={w} isInterest={prefSet.has(w.category)} paid={mine.paid.has(w.id)} saved={mine.saved.has(w.id)} />)}
              </div>
            )}
            {!loading && visible.length > 0 && (page === 1 ? (
              endIdx < shown.length && <LoadMoreButton onClick={loadMore} from={1} to={endIdx} total={shown.length} />
            ) : (
              <CatalogPager page={page} totalPages={totalPages} onPage={goToPage} from={startIdx + 1} to={endIdx} total={shown.length} />
            ))}
          </div>
        </div>
      </section>

      <PreferencesModal open={showPrefs} categories={categories} initial={prefs}
        onOpenChange={(o) => { setShowPrefs(o); if (!o) sessionStorage.setItem("prefs-skipped", "1"); }}
        onSaved={() => {}} />

      <CommunitySection />

      <MaestroCta />
    </div>
  );
}

const CTA_FEATURES = [
  { id: "sesiones", icon: CalendarClock, title: "Gestión flexible", text: "Estructura fechas, horarios e itinerarios de tus talleres." },
  { id: "cobro", icon: Store, title: "Vitrina inmediata", text: "Haz el pago de publicación y tu taller aparecerá de inmediato en nuestra vitrina, listo para captar la atención de nuevos alumnos." },
  { id: "contacto", icon: MessageCircle, title: "Contacto directo", text: "Resuelve dudas de alumnos interesados y cierra más inscripciones." },
  { id: "promociones", icon: Megaphone, title: "Promociona tus talleres", text: "Crea ofertas con tiempo limitado que se activan solas." },
];

function MaestroCta() {
  return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-4">
        <div className="rounded-3xl bg-primary text-primary-foreground p-10 sm:p-14 grain-overlay relative overflow-hidden">
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">¿Tienes un taller? Llénalo de alumnos.</h2>
            <p className="mt-4 text-primary-foreground/80 text-lg max-w-3xl" data-testid="cta-text">
              Gestiona fechas, controla asistencia, aplica descuentos y flexibiliza tu negocio publicando tus talleres de forma simple.
            </p>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
              {CTA_FEATURES.map((f) => (
                <div key={f.id} className="rounded-2xl bg-primary-foreground/10 border border-primary-foreground/15 p-4" data-testid={`cta-feature-${f.id}`}>
                  <f.icon className="w-6 h-6 mb-2" />
                  <p className="font-semibold">{f.title}</p>
                  <p className="text-sm text-primary-foreground/70 mt-1">{f.text}</p>
                </div>
              ))}
            </div>
            <a href="/registro?rol=maestro">
              <Button variant="secondary" size="lg" className="mt-8 gap-2" data-testid="cta-publicar">
                Publicar mi Taller <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
  );
}
